import { useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { SCRATCH_LIMITS, type ScratchInput, type ScratchProject } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { useOpsMutation } from '@/components/ops/api'
import { errorMessage, FormError } from '@/components/app/states'
import { checkFile, IMAGE_ACCEPT, LOGO_ACCEPT, ScratchForm, uploadFile } from '@/components/ops/scratch'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export const Route = createFileRoute('/ops/scratch/new')({
  head: () => ({ meta: [{ title: 'New site · Ops' }] }),
  component: NewScratchPage,
})

function NewScratchPage() {
  const navigate = useNavigate()
  const { run, pending, error } = useOpsMutation()
  const [logo, setLogo] = useState<File | null>(null)
  const [images, setImages] = useState<File[]>([])
  const [fileError, setFileError] = useState<string | null>(null)
  const [progress, setProgress] = useState<string | null>(null)
  // Set when the project was saved but some files did not upload.
  const [partial, setPartial] = useState<{ id: string; failures: string[] } | null>(null)

  function pickImages(list: FileList | null) {
    const files = [...(list ?? [])]
    const bad = files.map((f) => checkFile(f, 'images')).find(Boolean)
    if (bad) return setFileError(bad)
    if (files.length > SCRATCH_LIMITS.images) return setFileError(`Choose at most ${SCRATCH_LIMITS.images} images.`)
    setFileError(null)
    setImages(files)
  }

  function pickLogo(f: File | undefined) {
    const bad = f ? checkFile(f, 'logo') : null
    setFileError(bad)
    setLogo(bad ? null : (f ?? null))
  }

  async function onSubmit(value: ScratchInput) {
    if (fileError) return
    const p = await run(() => api<ScratchProject>('/ops/scratch', { method: 'POST', json: value }), { invalidate: false })
    if (!p) return
    const failures: string[] = []
    const files = images.map((f): ['logo' | 'images', File] => ['images', f])
    if (logo) files.unshift(['logo', logo])
    for (const [i, [kind, f]] of files.entries()) {
      setProgress(`Uploading ${i + 1} of ${files.length}: ${f.name}`)
      await uploadFile(p.id, kind, f).catch((e) => failures.push(`${f.name}: ${errorMessage(e)}`))
    }
    setProgress(null)
    if (failures.length) setPartial({ id: p.id, failures })
    else await navigate({ to: '/ops/scratch/$projectId', params: { projectId: p.id } })
  }

  if (partial) {
    return (
      <Alert className="max-w-2xl">
        <AlertTitle>Project saved, some files did not upload</AlertTitle>
        <AlertDescription>
          <ul className="list-disc pl-4">
            {partial.failures.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
          <Link to="/ops/scratch/$projectId" params={{ projectId: partial.id }} className="mt-2 inline-block font-medium underline underline-offset-4">
            Open the project to upload them again
          </Link>
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h1 className="text-base font-semibold">New site from scratch</h1>
        <p className="text-muted-foreground">The brief for a site built on our infrastructure. Nothing is built yet; the project is saved as a draft.</p>
      </div>
      <ScratchForm submitLabel={progress ? 'Uploading…' : 'Save draft'} pending={pending || !!progress} error={error} onSubmit={onSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="scratch-logo">Logo (optional)</Label>
            <Input id="scratch-logo" type="file" accept={LOGO_ACCEPT} onChange={(e) => pickLogo(e.target.files?.[0])} aria-describedby="scratch-logo-hint" />
            <p id="scratch-logo-hint" className="text-xs text-muted-foreground">
              PNG, JPEG, WebP, GIF or SVG, up to {SCRATCH_LIMITS.logoBytes / 1024 / 1024} MB.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="scratch-images">Images (optional)</Label>
            <Input id="scratch-images" type="file" accept={IMAGE_ACCEPT} multiple onChange={(e) => pickImages(e.target.files)} aria-describedby="scratch-images-hint" />
            <p id="scratch-images-hint" className="text-xs text-muted-foreground">
              {images.length ? `${images.length} selected. ` : ''}Up to {SCRATCH_LIMITS.images} PNG, JPEG, WebP or GIF files, {SCRATCH_LIMITS.imageBytes / 1024 / 1024} MB each.
            </p>
          </div>
        </div>
        <FormError message={fileError} />
        {progress && (
          <p role="status" className="text-sm text-muted-foreground">
            {progress}
          </p>
        )}
      </ScratchForm>
    </div>
  )
}
